import React, { useCallback, useId, useState } from 'react';

import FeedbackCollector from '@atlaskit/feedback-collector/FeedbackCollector';
import { Fieldset } from '@atlaskit/form/fieldset';
import { Label } from '@atlaskit/form/label/default';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';
import Link from '@atlaskit/link/link';
import { Stack } from '@atlaskit/primitives/compiled';
import RadioGroup from '@atlaskit/radio/radio-group';
import TextArea from '@atlaskit/textarea/text-area';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

import { useFlagActions } from '@townsquare/flags';
import { TypeIcon } from '@townsquare/flags/type-icon';
import { useModalActions } from '@townsquare/modals';
import { useUserStore } from '@townsquare/user-store';

const ENTRYPOINT_ID = '4375e179-9f41-4079-a383-38525b53c6d5';
const FEEDBACK_FLAG_ID = 'finesse-feedback-submitted-flag';
const SURVEY_CONTEXT_KEY = 'homeUnderstandingAndNavigationEase';
const FINESSE_CHANGES_URL =
	'https://hello.atlassian.net/wiki/spaces/DST/pages/7562543245/Project+Poster+Hierarchy+Finesse#What%E2%80%99s-getting-changed';

const newWindowMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-collector.new-window',
	defaultMessage: '(opens in a new tab)',
	description: 'Screen reader notice that the Finesse documentation link opens in a new tab.',
});

const homeEaseQuestionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.home-ease-question',
	defaultMessage: 'Compared with before, how easy is it to understand and navigate Home?',
	description:
		'Question asking users to rate how easy Atlassian Home is to understand and navigate.',
});

const muchEasierOptionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.much-easier-option',
	defaultMessage: 'Much easier',
	description:
		'Survey option indicating Atlassian Home feels much easier to understand and navigate.',
});

const aLittleEasierOptionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.a-little-easier-option',
	defaultMessage: 'A little easier',
	description:
		'Survey option indicating Atlassian Home feels a little easier to understand and navigate.',
});

const aboutTheSameOptionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.about-the-same-option',
	defaultMessage: 'About the same',
	description: 'Survey option indicating Atlassian Home feels about the same as before.',
});

const aLittleHarderOptionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.a-little-harder-option',
	defaultMessage: 'A little harder',
	description:
		'Survey option indicating Atlassian Home feels a little harder to understand and navigate.',
});

const muchHarderOptionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.much-harder-option',
	defaultMessage: 'Much harder',
	description:
		'Survey option indicating Atlassian Home feels much harder to understand and navigate.',
});

const noChangesNoticedOptionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.no-changes-noticed-option',
	defaultMessage: 'I don’t notice any changes',
	description: 'Survey option indicating the user does not notice any changes in Atlassian Home.',
});

const feedbackDetailsMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.feedback-details',
	defaultMessage: 'What, if anything, feels clearer or harder to use?',
	description:
		'Label for details about how updated interface elements feel clearer or harder to use.',
});

const feedbackSummaryMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-collector.summary',
	defaultMessage: 'Home feedback: {answer}',
	description:
		'Fallback Jira issue summary using the selected survey answer when optional feedback details are blank.',
});

const submittedTitleMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-collector.submitted-title',
	defaultMessage: 'Thank you for your feedback!',
	description: 'Title of the confirmation flag after Finesse feedback is submitted.',
});

const submittedDescriptionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-collector.submitted-description',
	defaultMessage: 'Your feedback has been submitted',
	description: 'Description of the confirmation flag after Finesse feedback is submitted.',
});

interface FinesseFeedbackCollectorProps {
	onClose: () => void;
}

export const FinesseFeedbackCollector = ({
	onClose,
}: FinesseFeedbackCollectorProps): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const [user] = useUserStore();
	const { showFlag } = useFlagActions();
	const [answer, setAnswer] = useState<string>();
	const [details, setDetails] = useState('');
	const questionLabelId = `finesse-feedback-question-label-${useId()}`;
	const detailsFieldId = `finesse-feedback-details-${useId()}`;
	const trimmedDetails = details.trim();
	const feedbackOptions = [
		{ label: formatMessage(muchEasierOptionMessage), value: 'much-easier' },
		{ label: formatMessage(aLittleEasierOptionMessage), value: 'a-little-easier' },
		{ label: formatMessage(aboutTheSameOptionMessage), value: 'about-the-same' },
		{ label: formatMessage(aLittleHarderOptionMessage), value: 'a-little-harder' },
		{ label: formatMessage(muchHarderOptionMessage), value: 'much-harder' },
		{
			label: formatMessage(noChangesNoticedOptionMessage),
			value: 'no-changes-noticed',
		},
	];
	const selectedAnswerLabel = feedbackOptions.find(({ value }) => value === answer)?.label;
	const summaryDefaultValue = (
		trimmedDetails ||
		(selectedAnswerLabel
			? formatMessage(feedbackSummaryMessage, { answer: selectedAnswerLabel })
			: '')
	)
		.replace(/\s+/g, ' ')
		.slice(0, 100);

	const afterSubmit = useCallback(() => {
		const title = formatMessage(submittedTitleMessage);
		const description = formatMessage(submittedDescriptionMessage);

		showFlag({
			id: FEEDBACK_FLAG_ID,
			title,
			description,
			icon: <TypeIcon type="success" />,
			isAutoDismiss: true,
		});
	}, [formatMessage, showFlag]);

	return (
		<FeedbackCollector
			additionalFields={[
				{
					id: 'customfield_10047',
					value: JSON.stringify(
						{
							accountId: user.accountId,
							feedbackTarget: 'Atlas - Navigation',
							townsquareApp: 'home',
							...(answer ? { surveyResponses: { [SURVEY_CONTEXT_KEY]: answer } } : {}),
						},
						null,
						2,
					),
				},
			]}
			atlassianAccountId={user.accountId}
			customContent={
				<Stack space="space.200">
					<Fieldset
						legend={
							<span id={questionLabelId}>
								{formatMessage(homeEaseQuestionMessage)}
								<RequiredAsterisk />
							</span>
						}
					>
						<RadioGroup
							isRequired
							labelId={questionLabelId}
							name={SURVEY_CONTEXT_KEY}
							onChange={(event) => setAnswer(event.currentTarget.value)}
							options={feedbackOptions}
							value={answer}
						/>
					</Fieldset>
					<div>
						<Label htmlFor={detailsFieldId}>{formatMessage(feedbackDetailsMessage)}</Label>
						<TextArea
							id={detailsFieldId}
							minimumRows={6}
							name="finesseFeedbackDetails"
							onChange={(event) => setDetails(event.currentTarget.value)}
							value={details}
						/>
					</div>
					<Link
						href={FINESSE_CHANGES_URL}
						target="_blank"
						rel="noopener noreferrer"
						newWindowLabel={formatMessage(newWindowMessage)}
					>
						<FormattedMessage
							id="townsquare.home.finesse-feedback-collector.learn-more"
							defaultMessage="Learn more about the Finesse visual updates."
							description="Link below the feedback survey questions to the section of the internal Finesse project poster explaining the visual changes."
						/>
					</Link>
				</Stack>
			}
			descriptionDefaultValue={trimmedDetails}
			disableSubmitButton={!answer}
			entrypointId={ENTRYPOINT_ID}
			feedbackTitleDetails={undefined}
			locale={locale}
			name={user.name}
			onClose={onClose}
			onSubmit={afterSubmit}
			showDefaultTextFields={false}
			showTypeField={false}
			summaryDefaultValue={summaryDefaultValue}
		/>
	);
};

export const useFinesseFeedbackModal = (): { openFinesseFeedbackModal: () => void } => {
	const { openModal, closeModal } = useModalActions();

	const openFinesseFeedbackModal = useCallback(() => {
		openModal('feedback-modal', {
			component: <FinesseFeedbackCollector onClose={() => closeModal('feedback-modal')} />,
		});
	}, [closeModal, openModal]);

	return { openFinesseFeedbackModal };
};
